import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';

import {
  QBox,
  QDatepicker,
  QFormControl,
  QInput,
  QMultiSelect,
  QSelect,
  QSelectItem,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';

import { useEligibleCollaborators } from '../../../hooks/useEligibleCollaborators';
import { useTags } from '../../../hooks/useTags';
import { useUsersOptions } from '../../../hooks/useUsersOptions';
import {
  EventTag,
  EventTemplateField,
  EventType,
  ProbabilityType,
  Product,
  QualityEvent,
  RiskType,
  RootCause,
  SeverityType,
  User,
} from '../../../types';
import { keyToTitle } from '../../../utils/textUtils';
import { ModalForm } from '../formHelpers';
import { DateTime } from 'luxon';
import { formatDatePickerComponentDateToString } from '../../../utils/datetimeUtils';
import { useFlags } from '../../../external/LaunchDarklyAdapter';
import { nativeCodeNameFilterOption } from '../../EventTable/components/codeNameFilterOption';

type Props = {
  formMethods: UseFormReturn<ModalForm>;
  onSubmit: any;
  data: {
    eventTypes: EventType[];
    eventTags: EventTag[];
    probabilities: ProbabilityType[];
    severities: SeverityType[];
    risks: RiskType[];
    users: User[];
    products: Product[];
    rootCauses: RootCause[];
  };
  event: QualityEvent;
  userId: number;
  optionalFields: EventTemplateField[] | [];
};

const EventForm: React.FC<Props> = ({ formMethods, onSubmit, data, event, userId, optionalFields }) => {
  const { t } = useTranslation();
  const isMultiSelectEventsListEnabled = useFlags('multiSelectEventsList');
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = formMethods;
  const eventTypesOptions: QSelectItem<React.ReactNode>[] = data.eventTypes
    .filter((eventType) => eventType.id !== String(event.workflow_id) && eventType.status === 'effective')
    .map((eventType) => ({
      value: String(eventType.id),
      label: eventType.name,
      code: isMultiSelectEventsListEnabled ? eventType.prefix : undefined,
    }));

  const eventTypeTimeLimit = Object.assign(
    {},
    ...data.eventTypes.map((x) => ({ [String(x.id)]: String(x.defaultTimeLimit) })),
  );
  const selectedCollaborators = watch('collaborators');
  const ownerCandidates = useMemo(() => {
    const collaboratorIds = new Set(selectedCollaborators);
    return data.users.filter(({ id }) => !collaboratorIds.has(String(id)));
  }, [data.users, selectedCollaborators]);
  const usersOptions = useUsersOptions(ownerCandidates, 'can_work_on_issue');

  const { data: tags, isLoading: isLoadingTags } = useTags();
  const eligibleCollaborators = useEligibleCollaborators(data.users, tags, event.tags_ids, Number(watch('owner_id')));
  const collaboratorsOptions = useUsersOptions(eligibleCollaborators);
  const hasPrefilledCollaborators = useRef(false);

  useEffect(() => {
    setValue('owner_id', String(userId));
    setValue('description', `Copied over from ${event.code}: ${event.description}`);
  }, [event.description, event.code, userId, setValue]);

  useEffect(() => {
    if (!isEventCollaboratorsEnabled || isLoadingTags || !data.users.length || hasPrefilledCollaborators.current) {
      return;
    }
    hasPrefilledCollaborators.current = true;
    const eligibleIds = new Set(eligibleCollaborators.map(({ id }) => id));
    setValue(
      'collaborators',
      event.collaborators
        .filter(({ id }) => eligibleIds.has(id) && String(id) !== getValues('owner_id'))
        .map(({ id }) => String(id)),
    );
  }, [
    isEventCollaboratorsEnabled,
    isLoadingTags,
    data.users.length,
    eligibleCollaborators,
    event.collaborators,
    setValue,
    getValues,
  ]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QStack direction="column" spacing={4}>
          <QFormControl
            label={t('common.labels.eventType')}
            error={errors.workflow_id?.message}
            isInvalid={!!errors.workflow_id}
            isRequired
            data-metrics="event-type-select"
          >
            <Controller
              control={control}
              name="workflow_id"
              render={({ field: { ref: _ref, onChange, ...field } }) => (
                <QSelect
                  {...field}
                  onChange={(changeObject) => {
                    if (!changeObject) {
                      return;
                    }

                    setValue('time_limit', eventTypeTimeLimit[changeObject.value]);
                    setValue(
                      'due_date',
                      formatDatePickerComponentDateToString(
                        DateTime.now().plus({ days: eventTypeTimeLimit[changeObject.value] }).toJSDate(),
                      ),
                    );
                    onChange(changeObject.value);
                  }}
                  options={eventTypesOptions}
                  filterOption={
                    isMultiSelectEventsListEnabled
                      ? (option, searchText) =>
                          option.data.value !== field.value && nativeCodeNameFilterOption(option, searchText)
                      : undefined
                  }
                />
              )}
            />
          </QFormControl>
          <QFormControl
            label={t('common.labels.title')}
            error={errors.title?.message}
            isInvalid={!!errors.title}
            data-metrics="event-title-input"
            isRequired
          >
            <Controller
              control={control}
              name="title"
              render={({ field: { ref: _ref, ...field } }) => <QInput {...field} />}
            />
          </QFormControl>
          <QFormControl
            label={t('common.labels.owner')}
            error={errors.owner_id?.message}
            isInvalid={!!errors.owner_id}
            data-metrics="event-owner-select"
            isRequired
          >
            <Controller
              control={control}
              name="owner_id"
              render={({ field: { ref: _ref, onChange, ...field } }) => (
                <QSelect
                  {...field}
                  onChange={(changeObject) => {
                    if (changeObject) {
                      onChange(changeObject.value);
                    }
                  }}
                  options={usersOptions}
                />
              )}
            />
          </QFormControl>
          {isEventCollaboratorsEnabled && (
            <QFormControl
              label={t('events.labels.collaborators')}
              data-metrics="event-collaborators-select"
              data-cy="escalate-event-collaborators-select"
            >
              <Controller
                control={control}
                name="collaborators"
                render={({ field: { ref: _ref, onChange, ...field } }) => (
                  <QMultiSelect
                    {...field}
                    onChange={(val) => onChange(val.map((c) => c.value))}
                    options={collaboratorsOptions}
                    isLoading={isLoadingTags}
                  />
                )}
              />
            </QFormControl>
          )}
          <QFormControl
            label={t('common.labels.dueDate')}
            data-metrics="event-due-date-input"
            isRequired
            isInvalid={Boolean(errors.due_date)}
            error={errors.due_date?.message}
          >
            <Controller
              control={control}
              name="due_date"
              render={({ field: { ref: _ref, ...field } }) => (
                <QDatepicker
                  {...field}
                  value={field.value ?? ''}
                  onChange={(date) => {
                    field.onChange(date ? formatDatePickerComponentDateToString(date) : date);
                  }}
                  minDate={new Date()}
                />
              )}
            />
          </QFormControl>
          <QFormControl label={t('common.labels.description')} data-metrics="event-description-input">
            <Controller
              control={control}
              name="description"
              render={({ field: { ref: _ref, ...field } }) => <QTextarea {...field} />}
            />
          </QFormControl>
          <QText>{t('events.messages.theFollowingEventFieldsWillBeCopied')}</QText>
          <QBox mt={0} pl={8}>
            <ul>
              <li>{t('common.labels.tags')}</li>
              <li>{t('events.labels.relatedDocuments')}</li>
              <li>{t('events.labels.relatedEvents')}</li>
              {optionalFields.map((field) => {
                switch (field.attribute_name) {
                  case 'risk':
                    return (
                      <>
                        <li key="probability">{t('common.labels.probability')}</li>
                        <li key="severity">{t('common.labels.severity')}</li>
                      </>
                    );
                  case 'supplier':
                    return <li key={field.attribute_name}>{t('common.labels.suppliers')}</li>;
                  default:
                    return <li key={field.attribute_name}>{keyToTitle(field.attribute_name)}</li>;
                }
              })}
            </ul>
          </QBox>
          <QText>{t('events.messages.youCanEditEventFieldsAfterCreation')}</QText>
        </QStack>
      </form>
    </FormProvider>
  );
};

export default EventForm;
